<template>
  <div class="placeholder-page">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-icon">
          <v-icon size="48" color="primary">{{ icon }}</v-icon>
        </div>
        <h1 class="page-title">{{ title }}</h1>
        <p class="page-description">{{ description }}</p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="page-content">
      <v-container>
        <!-- Coming Soon Card -->
        <v-row justify="center">
          <v-col cols="12" md="8" lg="6">
            <v-card class="coming-soon-card" elevation="8">
              <v-card-text class="pa-8 text-center">
                <div class="coming-soon-icon">
                  <v-icon size="80" color="primary" class="mb-6">mdi-rocket-launch</v-icon>
                </div>
                <h2 class="coming-soon-title">Coming Soon</h2>
                <p class="coming-soon-description">
                  We're working hard to bring you this feature. Stay tuned for updates!
                </p>
                
                <!-- Feature Preview -->
                <div class="feature-preview">
                  <div class="preview-item">
                    <v-icon size="24" color="success" class="mb-2">mdi-check-circle</v-icon>
                    <span class="preview-text">Advanced functionality</span>
                  </div>
                  <div class="preview-item">
                    <v-icon size="24" color="success" class="mb-2">mdi-check-circle</v-icon>
                    <span class="preview-text">Real-time updates</span>
                  </div>
                  <div class="preview-item">
                    <v-icon size="24" color="success" class="mb-2">mdi-check-circle</v-icon>
                    <span class="preview-text">Mobile optimized</span>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="action-buttons">
                  <v-btn
                    color="primary"
                    size="large"
                    class="mr-4"
                    @click="goBack"
                  >
                    <v-icon left>mdi-arrow-left</v-icon>
                    Go Back
                  </v-btn>
                  <v-btn
                    outlined
                    size="large"
                    @click="notifyMe"
                  >
                    <v-icon left>mdi-bell</v-icon>
                    Notify Me
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Related Features -->
        <v-row class="mt-8">
          <v-col cols="12">
            <h3 class="section-title">Related Features</h3>
          </v-col>
          <v-col cols="12" md="4" v-for="feature in relatedFeatures" :key="feature.title">
            <v-card class="feature-card" elevation="4" @click="navigateTo(feature.route)">
              <v-card-text class="pa-6">
                <div class="feature-icon">
                  <v-icon size="32" :color="feature.color">{{ feature.icon }}</v-icon>
                </div>
                <h4 class="feature-title">{{ feature.title }}</h4>
                <p class="feature-description">{{ feature.description }}</p>
                <v-btn
                  text
                  color="primary"
                  class="mt-4"
                  @click.stop="navigateTo(feature.route)"
                >
                  Explore
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlaceholderView',
  props: {
    title: {
      type: String,
      default: 'Feature Coming Soon'
    },
    description: {
      type: String,
      default: 'This feature is currently under development and will be available soon.'
    },
    icon: {
      type: String,
      default: 'mdi-cog'
    }
  },
  data() {
    return {
      relatedFeatures: [
        {
          title: 'Dashboard',
          description: 'View your main dashboard with key metrics and insights',
          icon: 'mdi-view-dashboard',
          color: 'primary',
          route: '/dashboard'
        },
        {
          title: 'Profile',
          description: 'Manage your account settings and preferences',
          icon: 'mdi-account-edit',
          color: 'info',
          route: '/profile'
        },
        {
          title: 'Help Center',
          description: 'Get support and find answers to common questions',
          icon: 'mdi-help-circle',
          color: 'success',
          route: '/help'
        }
      ]
    };
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    notifyMe() {
      this.$store.dispatch('showSuccess', 'You\'ll be notified when this feature is ready!');
    },
    navigateTo(route) {
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.placeholder-page {
  min-height: 100vh;
  background: #f8fafc;
}

/* Header Section */
.page-header {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 3rem 0;
}

.header-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.header-icon {
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
}

.page-description {
  font-size: 1.125rem;
  color: #718096;
  line-height: 1.6;
}

/* Main Content */
.page-content {
  padding: 3rem 0;
}

.coming-soon-card {
  border-radius: 16px;
  background: white;
  transition: all 0.3s ease;
}

.coming-soon-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.coming-soon-icon {
  margin-bottom: 2rem;
}

.coming-soon-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
}

.coming-soon-description {
  font-size: 1.125rem;
  color: #718096;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.feature-preview {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.preview-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.preview-text {
  font-size: 0.875rem;
  color: #4a5568;
  font-weight: 500;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

/* Related Features */
.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 2rem;
  text-align: center;
}

.feature-card {
  border-radius: 12px;
  background: white;
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
}

.feature-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.feature-icon {
  margin-bottom: 1rem;
}

.feature-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.feature-description {
  font-size: 0.875rem;
  color: #718096;
  line-height: 1.5;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .page-header {
    padding: 2rem 0;
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  .page-description {
    font-size: 1rem;
  }
  
  .page-content {
    padding: 2rem 0;
  }
  
  .coming-soon-title {
    font-size: 1.75rem;
  }
  
  .feature-preview {
    gap: 1rem;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .action-buttons .v-btn {
    width: 100%;
    max-width: 200px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.75rem;
  }
  
  .coming-soon-title {
    font-size: 1.5rem;
  }
  
  .feature-preview {
    flex-direction: column;
    gap: 1rem;
  }
}
</style> 